import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { useFormik } from 'formik';
import {
    AlertTriangle,
    ArrowDown,
    ArrowUp,
    Check,
    Eye,
    Package,
    PackagePlus,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import * as Yup from 'yup';
import { httpClient } from '../../common/httpclient';
import { sidebarExpandedSelector } from '../../component/layout/sidebarState';
import PageScaffold from '../../component/pageScaffold/PageScaffold';
import StatusChip from '../../component/common/StatusChip';
import usePermissions from '../../hooks/usePermissions';
import { useIsMobileShell } from '../../hooks/useIsMobileShell';
import { useAppBarTitle } from '../../component/layout/AppBarActions';
import MobileProductReposition from '../../component/product/MobileProductReposition';

/**
 * Barra de 3 zonas (baixo/médio/alto) com um marcador mostrando o valor atual
 * contra as faixas — DESIGN-SYSTEM.md §5.12 "3-zone level bar". Extraída
 * localmente (não há outro consumidor deste padrão no app ainda); ver nota
 * de extensão adicionada ao DESIGN-SYSTEM.md sobre o novo padrão desta tela
 * (radio-cards de movimentação + card de prévia de movimentação).
 */
function StockLevelBar({ markerLeft, sx }) {
    return (
        <Box sx={{ height: 10, borderRadius: '8px', overflow: 'hidden', display: 'flex', backgroundColor: 'background.subtle', position: 'relative', mt: '6px', ...sx }}>
            <Box sx={{ width: '20%', backgroundColor: 'error.bg' }} />
            <Box sx={{ width: '35%', backgroundColor: 'warning.bg' }} />
            <Box sx={{ width: '45%', backgroundColor: 'success.bg' }} />
            <Box
                sx={{
                    position: 'absolute',
                    top: -4,
                    width: 4,
                    height: 18,
                    borderRadius: '3px',
                    backgroundColor: 'text.primary',
                    boxShadow: (t) => `0 0 0 3px ${t.palette.background.paper}`,
                    left: markerLeft,
                    transition: 'left .25s ease',
                }}
            />
        </Box>
    );
}

/** Radio-card de tipo de movimento (Entrada/Saída) — cores por §4 (StatusChip domain="stockMovement"). */
function MoveCard({ selected, tone, icon: Icon, title, description, onSelect }) {
    const toneMain = `${tone}.main`;
    const toneBg = `${tone}.bg`;
    return (
        <Box
            role="radio"
            aria-checked={selected}
            tabIndex={0}
            onClick={onSelect}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onSelect();
                }
            }}
            sx={{
                position: 'relative',
                border: '1.5px solid',
                borderColor: selected ? toneMain : 'divider',
                borderRadius: '14px',
                p: '16px 18px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1.5,
                backgroundColor: selected ? toneBg : 'background.subtle',
                outline: 'none',
                transition: 'border-color .15s, background-color .15s',
                '&:hover': { borderColor: selected ? toneMain : 'text.disabled' },
                '&:focus-visible': { boxShadow: (t) => `0 0 0 3px ${t.palette.info.bg}` },
            }}
        >
            <Box
                sx={{
                    width: 38,
                    height: 38,
                    borderRadius: '10px',
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: selected ? toneMain : 'background.paper',
                    color: selected ? '#fff' : 'text.disabled',
                    border: '1px solid',
                    borderColor: selected ? toneMain : 'divider',
                    transition: 'background-color .15s, color .15s',
                }}
            >
                <Icon size={18} strokeWidth={2} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: '13.5px', fontWeight: 700, mb: '2px' }}>{title}</Typography>
                <Typography sx={{ fontSize: '11.5px', color: 'text.disabled' }}>{description}</Typography>
            </Box>
            {selected && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: 10,
                        right: 10,
                        width: 18,
                        height: 18,
                        borderRadius: '50%',
                        backgroundColor: toneMain,
                        color: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <Check size={10} strokeWidth={3} />
                </Box>
            )}
        </Box>
    );
}

const clampPct = (v) => Math.max(0, Math.min(100, v));
const fmtQty = (n) => `${n} un`;

// DESIGN-SYSTEM.md §5.5 "Floating label" (`.ff-input`/`.ff-label`) — same
// accordion-insertion-form field look already applied to Novo Produto
// (`pages/products/Form.js` `standaloneFieldSx`): 52px height, 12px radius,
// `--surface-2` fill, no native `<legend>` notch (label lives fully inside
// the box instead of cutting the border), 1.5px border. The app-wide theme
// (`theme.js`) only covers the generic MUI outlined box (radius/colors) and
// the shrink-state label typography — this box sizing/padding is specific to
// this denser field flavor (as opposed to the 40px `tb-field` toolbar-filter
// flavor used elsewhere), so it stays a local override here too, mirroring
// Form.js instead of changing the shared `MuiOutlinedInput` default.
// Ref: prototipo-reposicao-estoque.html `.ff-input`/`.ff-label` (~L228-249).
const floatingInputBoxSx = {
    '& .MuiOutlinedInput-root': {
        borderRadius: '12px',
        backgroundColor: 'background.subtle',
        height: '52px',
        transition: 'background-color .15s ease, box-shadow .15s ease',
        '&.MuiInputBase-multiline': {
            height: 'auto',
            minHeight: '96px',
            alignItems: 'flex-start',
            padding: '24px 14px 7px 14px',
        },
    },
    '& .MuiOutlinedInput-input:not(textarea)': {
        padding: '21px 14px 7px 14px',
    },
    '& .MuiOutlinedInput-notchedOutline': {
        borderWidth: '1.5px',
        borderColor: 'divider',
    },
    '& .MuiOutlinedInput-notchedOutline legend': {
        display: 'none',
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: 'primary.main',
        borderWidth: '1.5px',
    },
    '& .MuiInputBase-multiline textarea': {
        resize: 'vertical',
    },
};

const floatingFieldSx = {
    '& .MuiInputBase-input': { fontSize: '13.5px' },
    '& .MuiInputLabel-root': {
        fontSize: '13.5px',
        color: 'text.disabled',
        transform: 'translate(14px, 17px) scale(1)',
    },
    '& .MuiInputLabel-root.MuiInputLabel-shrink': {
        fontSize: '10px',
        fontWeight: 800,
        color: 'primary.main',
        letterSpacing: '.04em',
        textTransform: 'uppercase',
        transform: 'translate(14px, 8px) scale(1)',
    },
    ...floatingInputBoxSx,
};

export default function Reposition() {
    const theme = useTheme();
    const navigate = useNavigate();
    const location = useLocation();
    const { id } = useParams();
    const { userData } = usePermissions();
    const isMobileShell = useIsMobileShell();
    useAppBarTitle(isMobileShell ? 'Repor estoque' : '');
    const isSidebarMobile = useMediaQuery('(max-width:1023.95px)');
    const isSidebarExpanded = useRecoilValue(sidebarExpandedSelector);
    const actionFooterLeft = isSidebarMobile ? 0 : (isSidebarExpanded ? 264 : 76);

    const [product, setProduct] = useState({});
    const [loading, setLoading] = useState(false);
    const [dataLoading, setDataLoading] = useState(true);
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [message, setMessage] = useState('');
    const [severity, setSeverity] = useState('success');

    const currentStock = product.quantity ?? 0;
    const minStock = product.min_quantity ?? 0;

    const validationSchema = Yup.object({
        new_quantity: Yup.number()
            .required('Quantidade é obrigatória')
            .min(1, 'Quantidade deve ser maior que 0')
            .test(
                'max-saida',
                'Quantidade maior que o estoque disponível',
                function (value) {
                    if (this.parent.type_moviment !== 'Out') return true;
                    if (value === undefined || value === null) return true;
                    return value <= currentStock;
                }
            ),
        type_moviment: Yup.string()
            .required('Tipo é obrigatório'),
        observations: Yup.string()
            .max(100, 'Observação deve ter no máximo 100 caracteres')
    });

    const formik = useFormik({
        initialValues: {
            type_moviment: '',
            new_quantity: '',
            observations: ''
        },
        validationSchema: validationSchema,
        onSubmit: async (values) => {
            setLoading(true);
            const payload = {
                ...values,
                product_id: id
            };

            try {
                const response = await httpClient.post('/stock/reposition', payload);
                if (response.status === 200) {
                    setMessage('Reposicao salva com sucesso.');
                    setSeverity('success');
                    setOpenSnackbar(true);
                    await fetchProduct();
                    formik.resetForm();
                }
            } catch (error) {
                console.error('Error submitting form:', error);
                setMessage('Erro ao salvar reposicao.');
                setSeverity('error');
                setOpenSnackbar(true);
            } finally {
                setLoading(false);
            }
        }
    });

    const fetchProduct = async () => {
        setDataLoading(true);
        try {
            const response = await httpClient.get(`/stock/edit/${id}`);
            if (response.status === 200) {
                const data = response.data || {};
                setProduct({
                    name: data.name,
                    code: data.code,
                    quantity: data.quantity,
                    min_quantity: data.min_quantity,
                    category_name: data.category_name,
                    supplier_name: data.supplier_name,
                });
            }
        } catch (error) {
            console.error('Error fetching product:', error);
        } finally {
            setDataLoading(false);
        }
    }

    useEffect(() => {
        fetchProduct();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Define automaticamente o tipo de movimento como "Entrada" quando a quantidade for 0
    useEffect(() => {
        if (currentStock === 0 && !dataLoading) {
            formik.setFieldValue('type_moviment', 'In');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentStock, dataLoading]);

    const breadcrumbs = [
        { title: 'Produtos', link: '/products' },
    ];

    // ---- Prévia ao vivo da movimentação (DESIGN-SYSTEM.md §5.5 "campo com
    // prévia ao vivo" + prototipo-reposicao-estoque.html) — derivado a cada
    // render a partir do formik.values, sem estado imperativo duplicado.
    const selectedType = formik.values.type_moviment;
    const isEntrada = selectedType === 'In';
    const isSaida = selectedType === 'Out';
    const rawQty = parseInt(formik.values.new_quantity, 10);
    const qty = Number.isNaN(rawQty) || rawQty < 0 ? 0 : rawQty;
    const delta = isEntrada ? qty : (isSaida ? -qty : 0);
    const afterValue = currentStock + delta;
    const invalidSaida = isSaida && afterValue < 0;
    const canConfirm = Boolean(selectedType) && qty > 0 && !invalidSaida;

    const maxRef = Math.max(currentStock * 2, minStock * 4, 10);
    const headerMarkerLeft = `${clampPct((currentStock / maxRef) * 100)}%`;
    const afterMarkerLeft = `${clampPct((Math.max(afterValue, 0) / maxRef) * 100)}%`;

    let afterColor = 'text.primary';
    let afterNote = 'Nenhuma alteração informada ainda.';
    if (qty > 0) {
        if (invalidSaida) {
            afterColor = 'error.main';
            afterNote = 'Resultado inválido — o estoque não pode ficar negativo.';
        } else if (afterValue <= minStock) {
            afterColor = 'warning.main';
            afterNote = 'Ficará abaixo (ou igual) ao estoque mínimo configurado.';
        } else {
            afterColor = 'success.main';
            afterNote = 'Estoque saudável após a movimentação.';
        }
    }

    const responsibleName = userData?.name || userData?.username || '—';
    const nowLabel = (() => {
        const now = new Date();
        return `${now.toLocaleDateString('pt-BR')} ${now.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
    })();

    if (dataLoading) {
        return (
            <PageScaffold
                titlePageTarget="Reposição"
                links={breadcrumbs}
                openSnackbar={openSnackbar}
                setOpenSnackbar={setOpenSnackbar}
                message={message}
                severity={severity}
            >
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
                    <CircularProgress size={40} thickness={4} />
                    <Typography sx={{ mt: 2, fontSize: '13.5px', color: 'text.secondary' }}>
                        Carregando produto...
                    </Typography>
                </Box>
            </PageScaffold>
        );
    }

    if (isMobileShell) {
        return (
            <PageScaffold
                titlePageTarget="Reposição"
                links={breadcrumbs}
                openSnackbar={openSnackbar}
                setOpenSnackbar={setOpenSnackbar}
                message={message}
                severity={severity}
            >
                <MobileProductReposition
                    formik={formik}
                    product={product}
                    currentStock={currentStock}
                    minStock={minStock}
                    loading={loading}
                    canConfirm={canConfirm}
                    invalidSaida={invalidSaida}
                    afterValue={afterValue}
                    onCancel={() => (location.state?.fromList ? navigate(-1) : navigate('/products'))}
                />
            </PageScaffold>
        );
    }

    return (
        <PageScaffold
            titlePageTarget="Reposição"
            links={breadcrumbs}
            openSnackbar={openSnackbar}
            setOpenSnackbar={setOpenSnackbar}
            message={message}
            severity={severity}
        >
            <Box sx={{ width: '100%', maxWidth: 1360, mx: 'auto', px: { xs: 2, md: 3 }, py: { xs: 2, md: 3 } }}>
                {/* page-head */}
                <Box sx={{ mb: 2.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Box
                            sx={{
                                width: 38,
                                height: 38,
                                borderRadius: '11px',
                                backgroundImage: theme.palette.gradient.brand,
                                color: '#fff',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                            }}
                        >
                            <PackagePlus size={18} strokeWidth={2} />
                        </Box>
                        <Typography sx={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.02em' }}>
                            Reposição de estoque
                        </Typography>
                    </Box>
                    <Typography sx={{ fontSize: '13.5px', color: 'text.secondary', mt: 0.75, ml: '48px' }}>
                        Registre uma entrada ou saída manual de estoque para este produto.
                    </Typography>
                </Box>

                {/* Cabeçalho do produto */}
                <Box
                    sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: (t) => `${t.customRadius.card}px`,
                        backgroundColor: 'background.paper',
                        boxShadow: (t) => t.customShadows.sm,
                        p: 2.5,
                        mb: 2.25,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '18px',
                        flexWrap: 'wrap',
                    }}
                >
                    <Box
                        sx={{
                            width: 64,
                            height: 64,
                            borderRadius: '14px',
                            flexShrink: 0,
                            backgroundColor: 'info.bg',
                            color: 'primary.main',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <Package size={28} strokeWidth={1.6} />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: { xs: 0, sm: 220 } }}>
                        <Typography sx={{ fontSize: 16, fontWeight: 800, mb: '4px' }}>
                            {product.name || 'Produto'}
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px', fontSize: '12.5px', color: 'text.secondary' }}>
                            <span>Código: <Box component="b" sx={{ color: 'text.primary' }}>{product.code || '—'}</Box></span>
                            <Box component="span" sx={{ color: 'text.disabled' }}>·</Box>
                            <span>Categoria: {product.category_name || '—'}</span>
                            <Box component="span" sx={{ color: 'text.disabled' }}>·</Box>
                            <span>Fornecedor: {product.supplier_name || '—'}</span>
                        </Box>
                    </Box>
                    <Box sx={{ minWidth: 200, flexShrink: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: '2px' }}>
                            <Typography sx={{ fontSize: '11.5px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em', color: 'text.disabled' }}>
                                Estoque atual
                            </Typography>
                            <Typography sx={{ fontSize: 24, fontWeight: 800, letterSpacing: '-0.02em' }}>
                                {fmtQty(currentStock)}
                            </Typography>
                        </Box>
                        <StockLevelBar markerLeft={headerMarkerLeft} />
                        <Typography sx={{ fontSize: 11, color: 'text.disabled', mt: '6px' }}>
                            Estoque mínimo configurado: {fmtQty(minStock)}.
                        </Typography>
                    </Box>
                </Box>

                {/* Formulário + prévia ao vivo */}
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.6fr 1fr' }, gap: '18px', alignItems: 'start' }}>
                    <Box
                        sx={{
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: (t) => `${t.customRadius.card}px`,
                            backgroundColor: 'background.paper',
                            boxShadow: (t) => t.customShadows.sm,
                            p: 2.5,
                        }}
                    >
                        <Typography sx={{ fontSize: '14.5px', fontWeight: 700, mb: '4px' }}>
                            Detalhes da movimentação
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: 'text.disabled', mb: 2.25 }}>
                            Selecione o tipo de movimento e informe a quantidade a ser lançada.
                        </Typography>

                        <Typography sx={{ fontSize: '10.5px', fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '.05em', mb: 1.25, display: 'block' }}>
                            Tipo de movimento <Box component="span" sx={{ color: 'error.main' }}>*</Box>
                        </Typography>
                        <Box role="radiogroup" aria-label="Tipo de movimento" sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: '14px', mb: formik.touched.type_moviment && formik.errors.type_moviment ? 1 : 2.75 }}>
                            <MoveCard
                                selected={isEntrada}
                                tone="success"
                                icon={ArrowUp}
                                title="Entrada"
                                description="Aumenta o estoque disponível (compra, devolução, ajuste)."
                                onSelect={() => formik.setFieldValue('type_moviment', 'In')}
                            />
                            <MoveCard
                                selected={isSaida}
                                tone="error"
                                icon={ArrowDown}
                                title="Saída"
                                description="Reduz o estoque disponível (perda, avaria, ajuste)."
                                onSelect={() => formik.setFieldValue('type_moviment', 'Out')}
                            />
                        </Box>
                        {formik.touched.type_moviment && formik.errors.type_moviment && (
                            <Typography variant="caption" color="error" sx={{ mb: 2, display: 'block' }}>
                                {formik.errors.type_moviment}
                            </Typography>
                        )}

                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                            <TextField
                                fullWidth
                                label="Quantidade"
                                type="number"
                                name="new_quantity"
                                value={formik.values.new_quantity}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={formik.touched.new_quantity && Boolean(formik.errors.new_quantity)}
                                helperText={formik.touched.new_quantity && formik.errors.new_quantity}
                                inputProps={{ min: 1 }}
                                sx={floatingFieldSx}
                            />
                            <TextField
                                fullWidth
                                label="Estoque atual (referência)"
                                value={fmtQty(currentStock)}
                                disabled
                                sx={floatingFieldSx}
                            />
                        </Box>
                        <Typography sx={{ fontSize: 11, color: 'text.disabled', mt: 1, mb: 2.5 }}>
                            Informe a quantidade movimentada. Para saída, o valor não pode ser maior que o estoque disponível.
                        </Typography>

                        <TextField
                            fullWidth
                            label="Observação (opcional)"
                            name="observations"
                            value={formik.values.observations}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.observations && Boolean(formik.errors.observations)}
                            helperText={
                                formik.touched.observations && formik.errors.observations
                                    ? formik.errors.observations
                                    : `${formik.values.observations?.length || 0}/100 caracteres`
                            }
                            multiline
                            rows={3}
                            inputProps={{ maxLength: 100 }}
                            sx={floatingFieldSx}
                        />
                    </Box>

                    {/* Prévia ao vivo */}
                    <Box sx={{ position: { md: 'sticky' }, top: { md: 20 } }}>
                        <Typography
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                fontSize: '10.5px',
                                fontWeight: 800,
                                textTransform: 'uppercase',
                                letterSpacing: '.06em',
                                color: 'text.disabled',
                                mb: 1.25,
                            }}
                        >
                            <Eye size={13} /> Prévia da movimentação
                        </Typography>

                        <Box
                            sx={{
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: (t) => `${t.customRadius.hero}px`,
                                backgroundColor: 'background.paper',
                                boxShadow: (t) => t.customShadows.md,
                                p: 2.5,
                                mb: 1.75,
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <Box sx={{ width: 40, height: 40, borderRadius: '10px', backgroundColor: 'info.bg', color: 'primary.main', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                    <Package size={18} strokeWidth={1.6} />
                                </Box>
                                <Box sx={{ minWidth: 0 }}>
                                    <Typography sx={{ fontSize: 13, fontWeight: 700, lineHeight: 1.3 }} noWrap>
                                        {product.name || 'Produto'}
                                    </Typography>
                                    <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>
                                        Estoque atual: <Box component="b" sx={{ color: 'text.primary' }}>{fmtQty(currentStock)}</Box>
                                    </Typography>
                                </Box>
                            </Box>

                            <Box sx={{ height: '1px', backgroundColor: 'divider', my: 2 }} />

                            {selectedType ? (
                                <>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, py: 1.5 }}>
                                        <Box
                                            sx={{
                                                width: 44,
                                                height: 44,
                                                borderRadius: '50%',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                flexShrink: 0,
                                                backgroundColor: isEntrada ? 'success.bg' : 'error.bg',
                                                color: isEntrada ? 'success.main' : 'error.main',
                                            }}
                                        >
                                            {isEntrada ? <ArrowUp size={20} strokeWidth={2} /> : <ArrowDown size={20} strokeWidth={2} />}
                                        </Box>
                                        <Box>
                                            <StatusChip domain="stockMovement" value={isEntrada ? 'entrada' : 'saida'} />
                                            <Typography sx={{ fontSize: 26, fontWeight: 800, letterSpacing: '-0.02em', color: isEntrada ? 'success.main' : 'error.main', mt: '2px' }}>
                                                {isEntrada ? '+' : '-'}{qty} un
                                            </Typography>
                                        </Box>
                                    </Box>

                                    <Box sx={{ height: '1px', backgroundColor: 'divider', my: 2 }} />

                                    <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em', color: 'text.disabled', textAlign: 'center', mb: '2px' }}>
                                        Estoque após a movimentação
                                    </Typography>
                                    <Typography sx={{ fontSize: 30, fontWeight: 800, letterSpacing: '-0.02em', textAlign: 'center', color: afterColor }}>
                                        {fmtQty(afterValue)}
                                    </Typography>
                                    <Typography sx={{ fontSize: 11, color: 'text.disabled', textAlign: 'center', mt: '4px' }}>
                                        {afterNote}
                                    </Typography>

                                    <StockLevelBar markerLeft={afterMarkerLeft} sx={{ maxWidth: 340, mx: 'auto', mt: 1.5 }} />

                                    {invalidSaida && (
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'flex-start',
                                                gap: '6px',
                                                fontSize: 12,
                                                color: 'error.main',
                                                backgroundColor: 'error.bg',
                                                p: '10px 12px',
                                                borderRadius: '10px',
                                                lineHeight: 1.4,
                                                mt: 1.75,
                                            }}
                                        >
                                            <AlertTriangle size={15} strokeWidth={2} style={{ flexShrink: 0, marginTop: 1 }} />
                                            <span>Quantidade informada é maior que o estoque disponível. Ajuste o valor para confirmar a saída.</span>
                                        </Box>
                                    )}
                                </>
                            ) : (
                                <Typography sx={{ fontSize: 12.5, color: 'text.disabled', textAlign: 'center', py: 2 }}>
                                    Selecione o tipo de movimento ao lado para ver a prévia.
                                </Typography>
                            )}
                        </Box>

                        <Box
                            sx={{
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: (t) => `${t.customRadius.hero}px`,
                                backgroundColor: 'background.paper',
                                p: '16px 20px',
                            }}
                        >
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '12.5px', py: '6px', color: 'text.secondary' }}>
                                <span>Tipo</span>
                                <Box component="b" sx={{ color: 'text.primary' }}>{isEntrada ? 'Entrada' : (isSaida ? 'Saída' : '—')}</Box>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '12.5px', py: '6px', color: 'text.secondary' }}>
                                <span>Quantidade</span>
                                <Box component="b" sx={{ color: 'text.primary' }}>{qty > 0 ? fmtQty(qty) : '—'}</Box>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '12.5px', py: '6px', color: 'text.secondary' }}>
                                <span>Responsável</span>
                                <Box component="b" sx={{ color: 'text.primary' }}>{responsibleName}</Box>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '12.5px', py: '6px', color: 'text.secondary' }}>
                                <span>Data/hora</span>
                                <Box component="b" sx={{ color: 'text.primary' }}>{nowLabel}</Box>
                            </Box>
                        </Box>
                    </Box>
                </Box>

                {/* Espaçador para compensar o rodapé fixo abaixo */}
                <Box sx={{ height: 'calc(84px + env(safe-area-inset-bottom, 0px))' }} aria-hidden="true" />

                <Box
                    sx={{
                        position: 'fixed',
                        left: actionFooterLeft,
                        right: 0,
                        bottom: 0,
                        zIndex: theme.zIndex.appBar,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 2,
                        flexWrap: 'wrap',
                        backgroundColor: 'background.paper',
                        borderTop: '1px solid',
                        borderColor: 'divider',
                        boxShadow: '0 -6px 20px rgba(15,23,42,.06)',
                        px: 3,
                        py: 1.75,
                        pl: 'calc(24px + env(safe-area-inset-left, 0px))',
                        pr: 'calc(24px + env(safe-area-inset-right, 0px))',
                        pb: 'calc(14px + env(safe-area-inset-bottom, 0px))',
                        transition: 'left .2s ease',
                    }}
                >
                    <Typography sx={{ fontSize: '12.5px', color: 'text.disabled' }}>
                        Campos com <Box component="span" sx={{ color: 'error.main' }}>*</Box> são obrigatórios.
                    </Typography>
                    <Stack direction="row" spacing={1.5}>
                        <Button
                            variant="outlined"
                            // Veio da lista: volta por histórico (POP) para a lista restaurar os filtros.
                            onClick={() => (location.state?.fromList ? navigate(-1) : navigate('/products'))}
                        >
                            Cancelar
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<Check size={15} strokeWidth={2.5} />}
                            onClick={formik.handleSubmit}
                            disabled={loading || !canConfirm}
                        >
                            {loading ? 'Confirmando...' : 'Confirmar movimentação'}
                        </Button>
                    </Stack>
                </Box>
            </Box>
        </PageScaffold>
    );
}
